import React, { useState, useEffect, useRef } from 'react';

/**
 * Modal Avanzado para Micro Texto de Seguridad
 * Usado en billetes, pasaportes y documentos oficiales de alta seguridad
 */
const MicroTextModal = ({ isOpen, onClose, onGenerate }) => {
    // Estados principales
    const [text, setText] = useState('ORIGINAL • AUTHORIZED • VALID • SECURITY • CERTIFIED • AUTHENTIC');
    const [fontSize, setFontSize] = useState(2); // px (0.2mm)
    const [dpi, setDpi] = useState('1200');
    const [fontFamily, setFontFamily] = useState('Saira Extra Condensed');
    const [fontWeight, setFontWeight] = useState('400');
    const [tracking, setTracking] = useState(0);
    const [color, setColor] = useState('#2c3e50');
    const [color2, setColor2] = useState('#34495e');

    // Efectos especiales
    const [enableRainbow, setEnableRainbow] = useState(false);
    const [enableEmboss, setEnableEmboss] = useState(true);
    const [enableInvert, setEnableInvert] = useState(false);
    const [enableOutline, setEnableOutline] = useState(false);
    const [enableGlow, setEnableGlow] = useState(false);
    const [glowColor, setGlowColor] = useState('#00ffff');
    const [glowIntensity, setGlowIntensity] = useState(5);

    // Configuración de línea
    const [lineType, setLineType] = useState('continuous'); // continuous, repeated, circular
    const [lineLength, setLineLength] = useState(200);
    const [lineAngle, setLineAngle] = useState(0);
    const [lineSpacing, setLineSpacing] = useState(10);

    // Características anti-falsificación
    const [enableGuillochePattern, setEnableGuillochePattern] = useState(false);
    const [guillocheComplexity, setGuillocheComplexity] = useState(3);
    const [enableMicroPerforations, setEnableMicroPerforations] = useState(false);
    const [perforationDensity, setPerforationDensity] = useState(50);

    // Características de autenticación
    const [enableSerialNumber, setEnableSerialNumber] = useState(false);
    const [serialPrefix, setSerialPrefix] = useState('SEC');
    const [serialFormat, setSerialFormat] = useState('alphanumeric'); // alphanumeric, numeric, custom
    const [enableTimestamp, setEnableTimestamp] = useState(false);
    const [timestampFormat, setTimestampFormat] = useState('iso'); // iso, unix, custom

    // Estados de preview
    const canvasRef = useRef(null);
    const [previewScale, setPreviewScale] = useState(3);

    useEffect(() => {
        if (isOpen && canvasRef.current) {
            updatePreview();
        }
    }, [isOpen, text, fontSize, fontFamily, fontWeight, tracking, color, enableRainbow, enableEmboss, lineType, lineAngle]);

    const updatePreview = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;

        // Limpiar canvas
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);

        // Configurar fuente
        const scaledFontSize = fontSize * previewScale;
        ctx.font = `${fontWeight} ${scaledFontSize}px "${fontFamily}"`;
        ctx.textBaseline = 'middle';

        // Aplicar efectos
        if (enableEmboss) {
            // Sombra para efecto de relieve
            ctx.shadowColor = 'rgba(255, 255, 255, 0.8)';
            ctx.shadowBlur = 1;
            ctx.shadowOffsetX = 0.5;
            ctx.shadowOffsetY = 0.5;
        }

        if (enableGlow) {
            ctx.shadowColor = glowColor;
            ctx.shadowBlur = glowIntensity;
        }

        const displayText = text || 'MICRO TEXT';
        const y = height / 2;

        if (lineType === 'continuous') {
            // Línea continua
            ctx.save();
            ctx.translate(width / 2, height / 2);
            ctx.rotate((lineAngle * Math.PI) / 180);

            if (enableRainbow) {
                // Efecto arcoíris
                const gradient = ctx.createLinearGradient(-width / 2, 0, width / 2, 0);
                gradient.addColorStop(0, '#ff0000');
                gradient.addColorStop(0.17, '#ff7f00');
                gradient.addColorStop(0.33, '#ffff00');
                gradient.addColorStop(0.5, '#00ff00');
                gradient.addColorStop(0.67, '#0000ff');
                gradient.addColorStop(0.83, '#4b0082');
                gradient.addColorStop(1, '#9400d3');
                ctx.fillStyle = gradient;
            } else {
                ctx.fillStyle = enableInvert ? '#ffffff' : color;
            }

            if (enableOutline) {
                ctx.strokeStyle = color2;
                ctx.lineWidth = 0.5;
                ctx.strokeText(displayText, -width / 4, 0);
            }

            ctx.fillText(displayText, -width / 4, 0);
            ctx.restore();

        } else if (lineType === 'repeated') {
            // Texto repetido
            const textWidth = ctx.measureText(displayText + ' ').width;
            const repetitions = Math.ceil(width / textWidth) + 2;

            ctx.save();
            ctx.translate(width / 2, height / 2);
            ctx.rotate((lineAngle * Math.PI) / 180);

            for (let i = 0; i < repetitions; i++) {
                const x = -width / 2 + i * textWidth;

                if (enableRainbow) {
                    const hue = (i * 30) % 360;
                    ctx.fillStyle = `hsl(${hue}, 100%, 50%)`;
                } else {
                    ctx.fillStyle = color;
                }

                if (enableOutline) {
                    ctx.strokeStyle = color2;
                    ctx.lineWidth = 0.5;
                    ctx.strokeText(displayText, x, 0);
                }

                ctx.fillText(displayText, x, 0);
            }
            ctx.restore();

        } else if (lineType === 'circular') {
            // Texto circular
            const radius = Math.min(width, height) * 0.35;
            const centerX = width / 2;
            const centerY = height / 2;
            const chars = displayText.split('');
            const angleStep = (Math.PI * 2) / chars.length;

            chars.forEach((char, i) => {
                const angle = i * angleStep + (lineAngle * Math.PI / 180);
                const x = centerX + radius * Math.cos(angle);
                const y = centerY + radius * Math.sin(angle);

                ctx.save();
                ctx.translate(x, y);
                ctx.rotate(angle + Math.PI / 2);

                if (enableRainbow) {
                    const hue = (i * 360 / chars.length) % 360;
                    ctx.fillStyle = `hsl(${hue}, 100%, 50%)`;
                } else {
                    ctx.fillStyle = color;
                }

                if (enableOutline) {
                    ctx.strokeStyle = color2;
                    ctx.lineWidth = 0.5;
                    ctx.strokeText(char, 0, 0);
                }

                ctx.fillText(char, 0, 0);
                ctx.restore();
            });
        }

        // Resetear sombras
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
    };

    const generateSerialNumber = () => {
        const timestamp = Date.now().toString(36).toUpperCase();
        const random = Math.random().toString(36).substring(2, 8).toUpperCase();

        switch (serialFormat) {
            case 'alphanumeric':
                return `${serialPrefix}-${timestamp}-${random}`;
            case 'numeric':
                return `${serialPrefix}-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
            case 'custom':
                return `${serialPrefix}-${timestamp}`;
            default:
                return `${serialPrefix}-${timestamp}-${random}`;
        }
    };

    const generateTimestamp = () => {
        const now = new Date();

        switch (timestampFormat) {
            case 'iso':
                return now.toISOString();
            case 'unix':
                return Math.floor(now.getTime() / 1000).toString();
            case 'custom':
                return now.toLocaleString('es-ES', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            default:
                return now.toISOString();
        }
    };

    const handleGenerate = () => {
        let finalText = text.trim();

        if (!finalText) {
            alert('Por favor ingresa el texto de micro seguridad');
            return;
        }

        // Añadir número de serie si está habilitado
        if (enableSerialNumber) {
            const serial = generateSerialNumber();
            finalText = `${finalText} [SN:${serial}]`;
        }

        // Añadir timestamp si está habilitado
        if (enableTimestamp) {
            const timestamp = generateTimestamp();
            finalText = `${finalText} [TS:${timestamp}]`;
        }

        onGenerate({
            text: finalText,
            fontSize: parseFloat(fontSize),
            dpi: parseInt(dpi),
            fontFamily,
            fontWeight,
            tracking: parseInt(tracking),
            color,
            color2,
            lineType,
            lineLength: parseInt(lineLength),
            lineAngle: parseInt(lineAngle),
            lineSpacing: parseInt(lineSpacing),
            effects: {
                rainbow: enableRainbow,
                emboss: enableEmboss,
                invert: enableInvert,
                outline: enableOutline,
                glow: enableGlow,
                glowColor,
                glowIntensity: parseInt(glowIntensity)
            },
            security: {
                guillochePattern: enableGuillochePattern,
                guillocheComplexity: parseInt(guillocheComplexity),
                microPerforations: enableMicroPerforations,
                perforationDensity: parseInt(perforationDensity)
            },
            authentication: {
                serialNumber: enableSerialNumber,
                timestamp: enableTimestamp
            },
            customType: 'microText',
            isSecurityFeature: true,
            securityLevel: 'professional',
            metadata: {
                createdAt: new Date().toISOString(),
                resolution: `${dpi} DPI`,
                physicalSize: `${fontSize * 0.1}mm`
            }
        });

        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden border border-slate-700">
                {/* Header */}
                <div className="p-6 border-b border-slate-700 bg-gradient-to-r from-green-900/50 to-emerald-900/50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                                <span className="text-3xl">🔍</span>
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold text-white">Micro Texto de Seguridad Avanzado</h3>
                                <p className="text-sm text-green-300">Tecnología utilizada en billetes y pasaportes • Visible con lupa 10x-50x</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-slate-400 hover:text-white transition-colors p-2 hover:bg-slate-700 rounded-lg"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[75vh] overflow-y-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Columna 1 - Configuración Básica */}
                        <div className="space-y-4">
                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-green-400 mb-3 flex items-center">
                                    <span className="mr-2">📝</span> Texto y Tipografía
                                </h4>

                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Texto de Micro Seguridad</label>
                                        <textarea
                                            value={text}
                                            onChange={(e) => setText(e.target.value)}
                                            rows={3}
                                            className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-xs font-mono focus:ring-2 focus:ring-green-500"
                                            placeholder="ORIGINAL • AUTHORIZED • VALID"
                                            style={{ fontFamily: fontFamily }}
                                        />
                                        <p className="text-xs text-slate-500 mt-1">🔍 Visible con lupa 10x-50x</p>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Tamaño (px)</label>
                                            <select
                                                value={fontSize}
                                                onChange={(e) => setFontSize(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-green-500"
                                            >
                                                <option value="1">1px (0.1mm) - Límite</option>
                                                <option value="2">2px (0.2mm) - Estándar</option>
                                                <option value="3">3px (0.3mm) - Legible</option>
                                                <option value="4">4px (0.4mm) - Visible</option>
                                                <option value="5">5px (0.5mm) - Límite visual</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Resolución DPI</label>
                                            <select
                                                value={dpi}
                                                onChange={(e) => setDpi(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-green-500"
                                            >
                                                <option value="600">600 DPI - Láser básico</option>
                                                <option value="1200">1200 DPI - Profesional</option>
                                                <option value="2400">2400 DPI - Industrial</option>
                                                <option value="4800">4800 DPI - Fotolitografía</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Fuente de Seguridad</label>
                                        <select
                                            value={fontFamily}
                                            onChange={(e) => setFontFamily(e.target.value)}
                                            className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-green-500"
                                        >
                                            <option value="Saira Extra Condensed">Saira Extra Condensed</option>
                                            <option value="Roboto Condensed">Roboto Condensed</option>
                                            <option value="IBM Plex Sans Condensed">IBM Plex Condensed</option>
                                            <option value="Share Tech Mono">Share Tech Mono (OCR)</option>
                                            <option value="Source Code Pro">Source Code Pro</option>
                                        </select>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Grosor</label>
                                            <select
                                                value={fontWeight}
                                                onChange={(e) => setFontWeight(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-green-500"
                                            >
                                                <option value="300">Light</option>
                                                <option value="400">Regular</option>
                                                <option value="600">SemiBold</option>
                                                <option value="700">Bold</option>
                                                <option value="900">Black</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Espaciado: {tracking}</label>
                                            <input
                                                type="range"
                                                min="-50"
                                                max="200"
                                                value={tracking}
                                                onChange={(e) => setTracking(parseInt(e.target.value))}
                                                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Color Primario</label>
                                            <input
                                                type="color"
                                                value={color}
                                                onChange={(e) => setColor(e.target.value)}
                                                className="w-full h-10 bg-slate-900 border border-slate-600 rounded-lg cursor-pointer"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Color Secundario</label>
                                            <input
                                                type="color"
                                                value={color2}
                                                onChange={(e) => setColor2(e.target.value)}
                                                className="w-full h-10 bg-slate-900 border border-slate-600 rounded-lg cursor-pointer"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Preview */}
                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-cyan-400 mb-3 flex items-center">
                                    <span className="mr-2">👁️</span> Vista Previa (Ampliada {previewScale}x)
                                </h4>
                                <div className="w-full h-32 bg-white border border-slate-600 rounded-lg overflow-hidden">
                                    <canvas ref={canvasRef} width="400" height="128" className="w-full h-full" />
                                </div>
                                <div className="mt-2">
                                    <label className="block text-xs text-slate-400 mb-1">Zoom de Preview: {previewScale}x</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max="10"
                                        value={previewScale}
                                        onChange={(e) => setPreviewScale(parseInt(e.target.value))}
                                        className="w-full h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Columna 2 - Efectos y Disposición */}
                        <div className="space-y-4">
                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-purple-400 mb-3 flex items-center">
                                    <span className="mr-2">✨</span> Efectos Visuales
                                </h4>

                                <div className="space-y-2">
                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableRainbow}
                                            onChange={(e) => setEnableRainbow(e.target.checked)}
                                            className="w-4 h-4 text-purple-600 bg-slate-900 border-slate-600 rounded focus:ring-purple-500"
                                        />
                                        <span className="text-sm text-slate-300">🌈 Efecto iridiscente (rainbow)</span>
                                    </label>

                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableEmboss}
                                            onChange={(e) => setEnableEmboss(e.target.checked)}
                                            className="w-4 h-4 text-purple-600 bg-slate-900 border-slate-600 rounded focus:ring-purple-500"
                                        />
                                        <span className="text-sm text-slate-300">📐 Relieve microscópico (emboss)</span>
                                    </label>

                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableInvert}
                                            onChange={(e) => setEnableInvert(e.target.checked)}
                                            className="w-4 h-4 text-purple-600 bg-slate-900 border-slate-600 rounded focus:ring-purple-500"
                                        />
                                        <span className="text-sm text-slate-300">⚫ Inversión de contraste</span>
                                    </label>

                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableOutline}
                                            onChange={(e) => setEnableOutline(e.target.checked)}
                                            className="w-4 h-4 text-purple-600 bg-slate-900 border-slate-600 rounded focus:ring-purple-500"
                                        />
                                        <span className="text-sm text-slate-300">⭕ Contorno de seguridad</span>
                                    </label>

                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableGlow}
                                            onChange={(e) => setEnableGlow(e.target.checked)}
                                            className="w-4 h-4 text-purple-600 bg-slate-900 border-slate-600 rounded focus:ring-purple-500"
                                        />
                                        <span className="text-sm text-slate-300">💫 Efecto de resplandor (glow)</span>
                                    </label>

                                    {enableGlow && (
                                        <div className="ml-6 mt-2 space-y-2 p-3 bg-slate-900/50 rounded-lg border border-slate-600">
                                            <div>
                                                <label className="block text-xs text-slate-400 mb-1">Color de Resplandor</label>
                                                <input
                                                    type="color"
                                                    value={glowColor}
                                                    onChange={(e) => setGlowColor(e.target.value)}
                                                    className="w-full h-8 bg-slate-900 border border-slate-600 rounded cursor-pointer"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-slate-400 mb-1">Intensidad: {glowIntensity}</label>
                                                <input
                                                    type="range"
                                                    min="1"
                                                    max="20"
                                                    value={glowIntensity}
                                                    onChange={(e) => setGlowIntensity(parseInt(e.target.value))}
                                                    className="w-full h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-amber-400 mb-3 flex items-center">
                                    <span className="mr-2">📏</span> Disposición del Texto
                                </h4>

                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Tipo de Línea</label>
                                        <select
                                            value={lineType}
                                            onChange={(e) => setLineType(e.target.value)}
                                            className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-amber-500"
                                        >
                                            <option value="continuous">Línea Continua</option>
                                            <option value="repeated">Texto Repetido</option>
                                            <option value="circular">Disposición Circular</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Ángulo: {lineAngle}°</label>
                                        <input
                                            type="range"
                                            min="-180"
                                            max="180"
                                            value={lineAngle}
                                            onChange={(e) => setLineAngle(parseInt(e.target.value))}
                                            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                        />
                                    </div>

                                    {lineType === 'repeated' && (
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Espaciado: {lineSpacing}px</label>
                                            <input
                                                type="range"
                                                min="5"
                                                max="50"
                                                value={lineSpacing}
                                                onChange={(e) => setLineSpacing(parseInt(e.target.value))}
                                                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Columna 3 - Seguridad Avanzada */}
                        <div className="space-y-4">
                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-red-400 mb-3 flex items-center">
                                    <span className="mr-2">🛡️</span> Anti-Falsificación
                                </h4>

                                <div className="space-y-3">
                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableGuillochePattern}
                                            onChange={(e) => setEnableGuillochePattern(e.target.checked)}
                                            className="w-4 h-4 text-red-600 bg-slate-900 border-slate-600 rounded focus:ring-red-500"
                                        />
                                        <span className="text-sm text-slate-300">🌀 Patrón Guilloche</span>
                                    </label>

                                    {enableGuillochePattern && (
                                        <div className="ml-6 p-3 bg-slate-900/50 rounded-lg border border-slate-600">
                                            <label className="block text-xs text-slate-400 mb-1">Complejidad: {guillocheComplexity}</label>
                                            <input
                                                type="range"
                                                min="1"
                                                max="10"
                                                value={guillocheComplexity}
                                                onChange={(e) => setGuillocheComplexity(parseInt(e.target.value))}
                                                className="w-full h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>
                                    )}

                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableMicroPerforations}
                                            onChange={(e) => setEnableMicroPerforations(e.target.checked)}
                                            className="w-4 h-4 text-red-600 bg-slate-900 border-slate-600 rounded focus:ring-red-500"
                                        />
                                        <span className="text-sm text-slate-300">⚫ Micro-perforaciones</span>
                                    </label>

                                    {enableMicroPerforations && (
                                        <div className="ml-6 p-3 bg-slate-900/50 rounded-lg border border-slate-600">
                                            <label className="block text-xs text-slate-400 mb-1">Densidad: {perforationDensity}%</label>
                                            <input
                                                type="range"
                                                min="10"
                                                max="100"
                                                value={perforationDensity}
                                                onChange={(e) => setPerforationDensity(parseInt(e.target.value))}
                                                className="w-full h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-blue-400 mb-3 flex items-center">
                                    <span className="mr-2">🔐</span> Autenticación
                                </h4>

                                <div className="space-y-3">
                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableSerialNumber}
                                            onChange={(e) => setEnableSerialNumber(e.target.checked)}
                                            className="w-4 h-4 text-blue-600 bg-slate-900 border-slate-600 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-slate-300">🔢 Número de Serie</span>
                                    </label>

                                    {enableSerialNumber && (
                                        <div className="ml-6 space-y-2 p-3 bg-slate-900/50 rounded-lg border border-slate-600">
                                            <div>
                                                <label className="block text-xs text-slate-400 mb-1">Prefijo</label>
                                                <input
                                                    type="text"
                                                    value={serialPrefix}
                                                    onChange={(e) => setSerialPrefix(e.target.value)}
                                                    className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"
                                                    placeholder="SEC"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-slate-400 mb-1">Formato</label>
                                                <select
                                                    value={serialFormat}
                                                    onChange={(e) => setSerialFormat(e.target.value)}
                                                    className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"
                                                >
                                                    <option value="alphanumeric">Alfanumérico</option>
                                                    <option value="numeric">Numérico</option>
                                                    <option value="custom">Personalizado</option>
                                                </select>
                                            </div>
                                        </div>
                                    )}

                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableTimestamp}
                                            onChange={(e) => setEnableTimestamp(e.target.checked)}
                                            className="w-4 h-4 text-blue-600 bg-slate-900 border-slate-600 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-slate-300">⏰ Marca de Tiempo</span>
                                    </label>

                                    {enableTimestamp && (
                                        <div className="ml-6 p-3 bg-slate-900/50 rounded-lg border border-slate-600">
                                            <label className="block text-xs text-slate-400 mb-1">Formato</label>
                                            <select
                                                value={timestampFormat}
                                                onChange={(e) => setTimestampFormat(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"
                                            >
                                                <option value="iso">ISO 8601</option>
                                                <option value="unix">Unix Timestamp</option>
                                                <option value="custom">Personalizado</option>
                                            </select>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Información Técnica */}
                            <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 rounded-lg p-4 border border-green-700/50">
                                <h4 className="text-sm font-bold text-green-300 mb-2">ℹ️ Especificaciones</h4>
                                <div className="space-y-1 text-xs text-slate-400">
                                    <p>• <strong>Tamaño físico:</strong> {fontSize * 0.1}mm ({fontSize}px @ {dpi} DPI)</p>
                                    <p>• <strong>Tecnología:</strong> Impresión láser de alta resolución</p>
                                    <p>• <strong>Verificación:</strong> Lupa 10x-50x</p>
                                    <p>• <strong>Aplicaciones:</strong> Billetes, pasaportes, documentos oficiales</p>
                                    <p>• <strong>Nivel de seguridad:</strong> Profesional / Clase B</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-700 bg-slate-900/50 flex justify-between items-center">
                    <div className="text-xs text-slate-500">
                        <p>🔍 Tecnología de Micro Impresión Profesional</p>
                        <p>Compatible con impresoras láser de alta resolución ({dpi}+ DPI)</p>
                    </div>
                    <div className="flex space-x-3">
                        <button
                            onClick={onClose}
                            className="px-6 py-2.5 text-sm font-medium text-slate-300 bg-slate-800 border border-slate-600 rounded-lg hover:bg-slate-700 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleGenerate}
                            className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 rounded-lg hover:from-green-700 hover:to-teal-700 transition-all shadow-lg shadow-green-500/50"
                        >
                            🔍 Crear Micro Texto de Seguridad
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MicroTextModal;
